import { Button } from "@/components/flexkit/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/podkit/dialog/Dialog";
import { toast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { ModelSelect, ProviderFields } from "@/components/runners/details/LLMIntegrationComponents";
import { LLMIntegrationRemoveModal } from "@/components/runners/details/LLMIntegrationRemoveModal";
import { parseProviderFromEndpoint, Provider } from "@/components/runners/details/types";
import { buildEndpointUrl } from "@/components/runners/details/llm-integration-utils";
import { Switch } from "@/components/Switch";
import type { TrackLocation } from "@/hooks/use-segment";
import { useUpdateLLMIntegration } from "@/queries/runner-configuration-queries";
import { formatError } from "@/utils/errors";
import { LLMIntegrationPhase, SupportedModel } from "gitpod-next-api/gitpod/v1/agent_pb";
import type { LLMIntegration } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { type FC, useCallback, useState as useReactState, useState } from "react";

type LLMIntegrationModalProps = {
    runnerId: string;
    integration: LLMIntegration;
    onClose: () => void;
    "data-track-location": TrackLocation;
};

export const LLMIntegrationModal: FC<LLMIntegrationModalProps> = ({
    runnerId,
    integration,
    onClose,
    "data-track-location": dataTrackLocation,
}) => {
    const [endpoint, setEndpoint] = useState(integration.endpoint);
    const [apiKey, setApiKey] = useState("");
    const [selectedModel, setSelectedModel] = useState<SupportedModel>(
        integration.models.length > 0 ? integration.models[0] : SupportedModel.OPUS_4_5,
    );
    const [isEnabled, setIsEnabled] = useState(
        integration.phase === LLMIntegrationPhase.LLM_INTEGRATION_PHASE_AVAILABLE,
    );

    const provider = parseProviderFromEndpoint(integration.endpoint);
    const [bedrockModelId, setBedrockModelId] = useState(
        provider === Provider.Bedrock ? integration.endpoint.replace("bedrock://", "") : "",
    );
    const [vertexRegion, setVertexRegion] = useState(
        provider === Provider.Vertex ? integration.endpoint.split("/")[2] || "" : "",
    );
    const [vertexModelVersion, setVertexModelVersion] = useState(
        provider === Provider.Vertex ? integration.endpoint.split("/")[3] || "" : "",
    );
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

    const updateIntegration = useUpdateLLMIntegration(runnerId);
    const [showRemove, setShowRemove] = useReactState(false);

    const handleToggle = useCallback(async (checked: boolean) => {
        setIsEnabled(checked);
        return Promise.resolve();
    }, []);

    const handleFileUpload = (content: string, fileName: string) => {
        setApiKey(content);
        setUploadedFileName(fileName);
    };

    const handleFileRemove = () => {
        setUploadedFileName(null);
        setApiKey("");
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate required fields per provider
        if (provider === Provider.Bedrock && bedrockModelId.trim() === "") {
            toast({
                title: "Model ID required",
                description: "Enter a Bedrock model ID (e.g., eu.anthropic.claude-sonnet-4-20250514-v1:0)",
            });
            return;
        }

        const trimmedEndpoint = buildEndpointUrl(
            provider,
            bedrockModelId,
            vertexRegion,
            vertexModelVersion,
            endpoint,
        ).trim();

        if (!trimmedEndpoint) {
            toast({
                title: "Endpoint required",
                description: "Please enter an LLM endpoint URL",
            });
            return;
        }

        // Provider-specific validation
        if (provider === Provider.Vertex) {
            if (!vertexRegion.trim() || !vertexModelVersion.trim()) {
                toast({
                    title: "Missing fields",
                    description: "Please provide both region and model version",
                });
                return;
            }
        }

        // In edit mode, API key/service file is optional (only validate if provided)
        // Bedrock uses AWS credentials, no API key validation needed

        try {
            await updateIntegration.mutateAsync({
                integrationId: integration.id,
                models: [selectedModel],
                endpoint: trimmedEndpoint,
                apiKey: provider === Provider.Bedrock ? "ignore" : apiKey.trim() || undefined,
                maxTokens: 0, // Use model default
                phase: isEnabled
                    ? LLMIntegrationPhase.LLM_INTEGRATION_PHASE_AVAILABLE
                    : LLMIntegrationPhase.LLM_INTEGRATION_PHASE_DISABLED,
            });

            toast({
                title: "LLM integration updated",
                description: "Your LLM integration has been successfully updated",
            });

            onClose();
        } catch (error) {
            toast({
                title: "Failed to update LLM integration",
                description: formatError(error),
            });
        }
    };

    return (
        <div className="flex flex-col gap-6" data-track-location={dataTrackLocation}>
            <DialogHeader>
                <DialogTitle>Edit LLM Integration</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3">
                        <Switch
                            id="enabled-toggle"
                            state={isEnabled ? "checked" : "unchecked"}
                            onToggle={handleToggle}
                        />
                        <label htmlFor="enabled-toggle" className="text-sm font-medium">
                            Enable Integration
                        </label>
                    </div>
                    <Text className="text-sm text-content-secondary">
                        When disabled, this integration cannot be used to make LLM requests
                    </Text>
                </div>

                <ProviderFields
                    provider={provider}
                    bedrockModelId={bedrockModelId}
                    onBedrockModelIdChange={setBedrockModelId}
                    vertexRegion={vertexRegion}
                    onVertexRegionChange={setVertexRegion}
                    vertexModelVersion={vertexModelVersion}
                    onVertexModelVersionChange={setVertexModelVersion}
                    endpoint={endpoint}
                    onEndpointChange={setEndpoint}
                    uploadedFileName={uploadedFileName}
                    onFileUpload={handleFileUpload}
                    onFileRemove={handleFileRemove}
                    apiKey={apiKey}
                    onApiKeyChange={setApiKey}
                    isEdit={true}
                />

                <ModelSelect selectedModel={selectedModel} onModelChange={setSelectedModel} />

                <div className="flex items-center justify-between gap-2 pt-4">
                    <Button
                        type="button"
                        variant="destructive"
                        onClick={() => setShowRemove(true)}
                        data-tracking-id="remove-llm-integration-llm-integration-modal"
                    >
                        Remove
                    </Button>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            data-tracking-id="cancel-edit-llm-integration-llm-integration-modal"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            loading={updateIntegration.isPending}
                            data-testid="update-llm-integration-button"
                            data-tracking-id="confirm-save-llm-integration-llm-integration-modal"
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </form>
            {showRemove && (
                <Dialog open onOpenChange={(o) => !o && setShowRemove(false)}>
                    <DialogContent className="max-w-[600px]" data-track-location={dataTrackLocation}>
                        <LLMIntegrationRemoveModal
                            runnerId={runnerId}
                            integration={integration}
                            onClose={() => setShowRemove(false)}
                            onRemoved={() => {
                                setShowRemove(false);
                                onClose();
                            }}
                            data-track-location={dataTrackLocation}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};
